/**
 * Suite Manager Drawer — CRUD UI for evaluation suites and their test cases.
 *
 * Opened from the Evaluations sub-tab's "Manage Suites" button. Lists all
 * suites (system + tenant-authored), supports create/edit/delete/clone, and
 * provides an inline case editor (prompt, expected, expected-tools, category).
 *
 * System suites are badged and their delete button is disabled; clone is the
 * intended path for customising a system suite's cases.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import {
  Copy,
  Loader2,
  Pencil,
  Plus,
  Save,
  Shield,
  Trash2,
  X,
} from 'lucide-react'
import { api, type ApiError } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import { useConfirmDialog } from '../../ui/ConfirmDialog.js'
import { ActionButton } from './ui.js'

// Field chrome inside the drawer — identical to the Guardrails / Agent /
// Safety / AI-config drawers.
const LABEL_CLASS =
  'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'
const FIELD_INPUT_CLASS =
  'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TestCase {
  name: string
  caseId?: string | undefined
  query: string
  systemPrompt?: string | undefined
  expected: string
  turns?: Array<{ content: string }> | undefined
  expectedTools?: string[] | undefined
  toolFilter?: string[] | undefined
  category?: string | undefined
}

interface Suite {
  id: string
  slug: string
  name: string
  description: string | null
  isSystem: boolean
  testCases: TestCase[]
  caseCount: number
  createdAt: string
  updatedAt: string
}

interface SuiteListItem {
  id: string
  slug: string
  name: string
  description: string | null
  isSystem: boolean
  caseCount: number
}

// ---------------------------------------------------------------------------
// Focus trap (same pattern as EvalRunDetailDrawer)
// ---------------------------------------------------------------------------

function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onEscape: () => void,
): void {
  useEffect(() => {
    if (!isOpen) return
    const drawer = ref.current
    if (!drawer) return

    const focusables = (): HTMLElement[] =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'input, textarea, button, select, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'))

    focusables()[0]?.focus()

    function handleKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault()
        onEscape()
        return
      }
      if (e.key !== 'Tab' || !drawer) return
      const list = focusables()
      if (list.length === 0) return
      const first = list[0]!
      const last = list[list.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return (): void => document.removeEventListener('keydown', handleKeydown)
  }, [isOpen, onEscape, ref])
}

// ---------------------------------------------------------------------------
// Case editor
// ---------------------------------------------------------------------------

function CaseEditor({
  caseData,
  index,
  onChange,
  onRemove,
}: {
  caseData: TestCase
  index: number
  onChange: (next: TestCase) => void
  onRemove: () => void
}): JSX.Element {
  return (
    <div className="rounded-sm border border-gray-200 bg-gray-50/50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          Case {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="rounded-sm p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
          aria-label="Remove case"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-2">
        <input
          type="text"
          value={caseData.name}
          onChange={(e) => onChange({ ...caseData, name: e.target.value })}
          placeholder="Case name (e.g. refund-policy)"
          className={FIELD_INPUT_CLASS}
        />
        <textarea
          value={caseData.query}
          onChange={(e) => onChange({ ...caseData, query: e.target.value })}
          placeholder="Prompt / query"
          rows={2}
          className={FIELD_INPUT_CLASS}
        />
        <textarea
          value={caseData.expected}
          onChange={(e) => onChange({ ...caseData, expected: e.target.value })}
          placeholder="Expected output"
          rows={2}
          className={FIELD_INPUT_CLASS}
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            value={caseData.category ?? ''}
            onChange={(e) => onChange({ ...caseData, category: e.target.value || undefined })}
            placeholder="Category (e.g. happy_path)"
            className={FIELD_INPUT_CLASS}
          />
          <input
            type="text"
            value={(caseData.expectedTools ?? []).join(', ')}
            onChange={(e) => {
              const tools = e.target.value
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean)
              onChange({ ...caseData, expectedTools: tools.length > 0 ? tools : undefined })
            }}
            placeholder="Expected tools (comma-separated)"
            className={FIELD_INPUT_CLASS}
          />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Suite editor form
// ---------------------------------------------------------------------------

function emptySuite(): { slug: string; name: string; description: string; testCases: TestCase[] } {
  return {
    slug: '',
    name: '',
    description: '',
    testCases: [{ name: 'case-1', query: '', expected: '' }],
  }
}

function SuiteEditor({
  suite,
  onSave,
  onCancel,
  saving,
}: {
  suite: { id?: string; slug: string; name: string; description: string; testCases: TestCase[] }
  onSave: (data: { slug: string; name: string; description: string; testCases: TestCase[] }) => void
  onCancel: () => void
  saving: boolean
}): JSX.Element {
  const [slug, setSlug] = useState(suite.slug)
  const [name, setName] = useState(suite.name)
  const [description, setDescription] = useState(suite.description)
  const [cases, setCases] = useState<TestCase[]>(suite.testCases)
  const isExisting = suite.id !== undefined

  function updateCase(idx: number, next: TestCase): void {
    setCases((prev) => prev.map((c, i) => (i === idx ? next : c)))
  }
  function removeCase(idx: number): void {
    setCases((prev) => prev.filter((_, i) => i !== idx))
  }
  function addCase(): void {
    setCases((prev) => [...prev, { name: `case-${prev.length + 1}`, query: '', expected: '' }])
  }

  const canSave = name.trim().length > 0 && cases.length > 0 && cases.every((c) => c.name.trim() && c.query.trim() && c.expected.trim())

  return (
    <div className="space-y-4">
      <div>
        <label className={LABEL_CLASS}>Suite name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Customer Support Accuracy Benchmark"
          className={FIELD_INPUT_CLASS}
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>
          Slug {isExisting && <span className="text-gray-400">(read-only for existing suites)</span>}
        </label>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="e.g. support_accuracy_v2"
          disabled={isExisting}
          className={FIELD_INPUT_CLASS}
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>Description (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="What this suite evaluates…"
          className={FIELD_INPUT_CLASS}
        />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Test cases ({cases.length})
          </label>
          <button
            type="button"
            onClick={addCase}
            className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf]/10 px-2 py-1 text-[11px] font-medium text-[#3454cf] transition-colors hover:bg-[#3454cf]/20"
          >
            <Plus className="h-3 w-3" />
            Add case
          </button>
        </div>
        <div className="space-y-2">
          {cases.map((c, i) => (
            <CaseEditor
              key={i}
              caseData={c}
              index={i}
              onChange={(next) => updateCase(i, next)}
              onRemove={() => removeCase(i)}
            />
          ))}
          {cases.length === 0 && (
            <p className="rounded-sm border border-dashed border-gray-200 py-4 text-center text-xs text-gray-400">
              No test cases. Add at least one.
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
        <ActionButton
          tone="primary"
          icon={<Save className="h-4 w-4" />}
          label={saving ? 'Saving…' : 'Save suite'}
          busy={saving}
          disabled={!canSave}
          onClick={() => onSave({ slug, name, description, testCases: cases })}
        />
        <ActionButton tone="secondary" icon={<X className="h-4 w-4" />} label="Cancel" onClick={onCancel} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Clone dialog
// ---------------------------------------------------------------------------

function CloneDialog({
  suite,
  onConfirm,
  onCancel,
  cloning,
}: {
  suite: SuiteListItem
  onConfirm: (slug: string, name: string) => void
  onCancel: () => void
  cloning: boolean
}): JSX.Element {
  const [slug, setSlug] = useState(`${suite.slug}_copy`)
  const [name, setName] = useState(`${suite.name} (Copy)`)
  const canClone = slug.trim().length > 0 && name.trim().length > 0 && /^[a-z0-9_-]+$/.test(slug)

  return (
    <div className="space-y-3 rounded-sm border border-[#3454cf]/30 bg-[#3454cf]/[0.03] p-4">
      <p className="text-xs text-gray-600">
        Clone <span className="font-semibold">{suite.name}</span> to produce an editable copy with the same test cases.
      </p>
      <div>
        <label className={LABEL_CLASS}>New suite name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={FIELD_INPUT_CLASS} />
      </div>
      <div>
        <label className={LABEL_CLASS}>New slug</label>
        <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} className={FIELD_INPUT_CLASS} />
      </div>
      <div className="flex items-center gap-2">
        <ActionButton
          tone="primary"
          icon={<Copy className="h-4 w-4" />}
          label={cloning ? 'Cloning…' : 'Clone'}
          busy={cloning}
          disabled={!canClone}
          onClick={() => onConfirm(slug, name)}
        />
        <ActionButton tone="secondary" icon={<X className="h-4 w-4" />} label="Cancel" onClick={onCancel} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main drawer
// ---------------------------------------------------------------------------

interface SuiteManagerDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** Notify parent to refresh its suite list after a mutation. */
  onMutated: () => void
}

export default function SuiteManagerDrawer({
  isOpen,
  onClose,
  onMutated,
}: SuiteManagerDrawerProps): JSX.Element | null {
  const toast = useToast()
  const { confirm, Dialog } = useConfirmDialog()
  const drawerRef = useRef<HTMLDivElement>(null)
  useFocusTrap(drawerRef, isOpen, onClose)

  const [suites, setSuites] = useState<SuiteListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [editingSuite, setEditingSuite] = useState<Suite | null>(null)
  const [creatingNew, setCreatingNew] = useState(false)
  const [cloningSuite, setCloningSuite] = useState<SuiteListItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [cloning, setCloning] = useState(false)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const res = await api.get<{ suites: SuiteListItem[] }>('/admin/ai/evaluations/suites')
      setSuites(res.suites ?? [])
    } catch (err) {
      toast.error('Failed to load suites', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    if (isOpen) void load()
  }, [isOpen, load])

  if (!isOpen) return null

  async function loadSuiteForEdit(id: string): Promise<void> {
    try {
      const suite = await api.get<Suite>(`/admin/ai/evaluations/suites/${id}`)
      setEditingSuite(suite)
      setCreatingNew(false)
      setCloningSuite(null)
    } catch (err) {
      toast.error('Failed to load suite', err instanceof Error ? err.message : String(err))
    }
  }

  async function handleSaveCreate(data: { slug: string; name: string; description: string; testCases: TestCase[] }): Promise<void> {
    setSaving(true)
    try {
      await api.post('/admin/ai/evaluations/suites', {
        slug: data.slug,
        name: data.name,
        description: data.description || undefined,
        testCases: data.testCases,
      })
      toast.success('Suite created', data.name)
      setCreatingNew(false)
      onMutated()
      void load()
    } catch (err) {
      const apiErr = err as ApiError
      if (apiErr?.status === 409) toast.error('Slug already in use', 'Choose a different slug.')
      else toast.error('Failed to create suite', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveEdit(data: { slug: string; name: string; description: string; testCases: TestCase[] }): Promise<void> {
    if (!editingSuite) return
    setSaving(true)
    try {
      await api.put(`/admin/ai/evaluations/suites/${editingSuite.id}`, {
        name: data.name,
        description: data.description || undefined,
        testCases: data.testCases,
      })
      toast.success('Suite updated', data.name)
      setEditingSuite(null)
      onMutated()
      void load()
    } catch (err) {
      toast.error('Failed to update suite', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  function handleDelete(suite: SuiteListItem): void {
    confirm({
      title: 'Delete Evaluation Suite',
      message: `Delete "${suite.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: () => void deleteSuite(suite),
    })
  }

  async function deleteSuite(suite: SuiteListItem): Promise<void> {
    try {
      await api.delete(`/admin/ai/evaluations/suites/${suite.id}`)
      toast.success('Suite deleted', suite.name)
      onMutated()
      void load()
    } catch (err) {
      const apiErr = err as ApiError
      if (apiErr?.status === 409) toast.error('Cannot delete system suite', 'Clone it instead to customise.')
      else toast.error('Failed to delete suite', err instanceof Error ? err.message : String(err))
    }
  }

  async function handleClone(slug: string, name: string): Promise<void> {
    if (!cloningSuite) return
    setCloning(true)
    try {
      await api.post(`/admin/ai/evaluations/suites/${cloningSuite.id}/clone`, { slug, name })
      toast.success('Suite cloned', name)
      setCloningSuite(null)
      onMutated()
      void load()
    } catch (err) {
      const apiErr = err as ApiError
      if (apiErr?.status === 409) toast.error('Slug already in use', 'Choose a different slug.')
      else toast.error('Failed to clone suite', err instanceof Error ? err.message : String(err))
    } finally {
      setCloning(false)
    }
  }

  // ---- Render ----

  const showList = !editingSuite && !creatingNew && !cloningSuite

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="suite-mgr-title">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl md:w-[640px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex min-w-0 items-center gap-2">
            <span id="suite-mgr-title" className="min-w-0 truncate text-sm font-semibold text-gray-900">
              {editingSuite ? `Edit: ${editingSuite.name}` : creatingNew ? 'New suite' : cloningSuite ? `Clone: ${cloningSuite.name}` : 'Manage Evaluation Suites'}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 pb-6">
          {showList && (
            <>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs text-gray-500">{suites.length} suite(s)</p>
                <button
                  type="button"
                  onClick={() => setCreatingNew(true)}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#2a45b0]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  New suite
                </button>
              </div>

              {loading ? (
                <div className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-300" />
                </div>
              ) : suites.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">No suites found.</p>
              ) : (
                <ul className="space-y-2">
                  {suites.map((s) => (
                    <li key={s.id} className="rounded-sm border border-gray-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="min-w-0 truncate text-sm font-semibold text-gray-900">{s.name}</span>
                            {s.isSystem && (
                              <span className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf]/10 px-1.5 py-0.5 text-[10px] font-medium text-[#3454cf]">
                                <Shield className="h-3 w-3" />
                                System
                              </span>
                            )}
                          </div>
                          {s.description && (
                            <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{s.description}</p>
                          )}
                          <p className="mt-1 font-mono text-[10px] text-gray-400">
                            {s.slug} · {s.caseCount} cases
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => void loadSuiteForEdit(s.id)}
                            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                            aria-label="Edit suite"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setCloningSuite(s)}
                            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                            aria-label="Clone suite"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(s)}
                            disabled={s.isSystem}
                            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            aria-label="Delete suite"
                            title={s.isSystem ? 'System suites cannot be deleted — clone to customise' : 'Delete suite'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {creatingNew && (
            <SuiteEditor
              suite={emptySuite()}
              onSave={(data) => void handleSaveCreate(data)}
              onCancel={() => setCreatingNew(false)}
              saving={saving}
            />
          )}

          {editingSuite && (
            <SuiteEditor
              suite={{
                id: editingSuite.id,
                slug: editingSuite.slug,
                name: editingSuite.name,
                description: editingSuite.description ?? '',
                testCases: editingSuite.testCases,
              }}
              onSave={(data) => void handleSaveEdit(data)}
              onCancel={() => setEditingSuite(null)}
              saving={saving}
            />
          )}

          {cloningSuite && (
            <CloneDialog
              suite={cloningSuite}
              onConfirm={(slug, name) => void handleClone(slug, name)}
              onCancel={() => setCloningSuite(null)}
              cloning={cloning}
            />
          )}
        </div>
        <Dialog />
      </div>
    </div>
  )
}
